import { Fragment, useRef } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import Papa from 'papaparse'

import DialogBody from 'components/Dialog/DialogBody'
import DialogTitle from 'components/Dialog/DialogTitle'
import DialogActions from 'components/Dialog/DialogActions'

import fileValidation from 'components/PosAccounts/utils/validation'
import uploadFileUtils from 'components/PosAccounts/utils/uploadFile'
import {
  orgAttendantsFileErrorMessagesCodes,
  orgAttendantsFileErrorMessages,
} from 'components/PosAccounts/utils'
import { UPLOAD_ORG_ATTENDANTS_STEPS } from 'resources'
import { ReactComponent as CloseIcon } from 'assets/icons/closeIcon.svg'

const ErrorsPreview = ({
  parsedFile,
  attendantsWithApiErrors,
  setStep,
  handleUploadDialogClose,
}) => {
  const downloadLinkRef = useRef()

  const attendantsWithClientSideErrors =
    fileValidation.orgAttendatsRowsWithErrors(parsedFile)

  const attendantsWithApiErrosAndErrors = [
    ...attendantsWithClientSideErrors,
    ...attendantsWithApiErrors,
  ]

  const {
    INVALID_PIN_LENGTH,
    DUPLICATE_PIN,
    INVALID_ROLE,
    EXISTING_PIN,
  } = orgAttendantsFileErrorMessagesCodes

  // We need to export the attendants with errors as CSV
  const handleExportAsCSV = () => {
    const attendantsWithHumanReadableErrors =
      uploadFileUtils.renderAttendantsWithHumanReadableErrors(
        attendantsWithApiErrosAndErrors
      )

    const csv = Papa.unparse(attendantsWithHumanReadableErrors)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)

    if (downloadLinkRef.current) {
      downloadLinkRef.current.href = url
      downloadLinkRef.current.download = 'attendants.csv'
      downloadLinkRef.current.click()
      URL.revokeObjectURL(url)
    }

    handleUploadDialogClose()
  }

  return (
    <>
      <DialogTitle>
        <header className="upload-file__header">
          <h3 className="upload-file__title">
            Upload Organization Attendants
          </h3>
          <a
            className="upload-file__header-link"
            href="https://support.venuenext.net/hc/en-us/articles/32114293251859-POS-Accounts"
          >
            Learn More
          </a>
          <button
            type="button"
            className="upload-file__close-dialog-icon"
            onClick={handleUploadDialogClose}
          >
            <CloseIcon />
          </button>
        </header>
        <p className="color-danger margin-top-sm margin-bottom-xxs">
          The following accounts were not created due to invalid data
          or non-unique PINs. Please export the accounts below and
          re-upload again.
        </p>
      </DialogTitle>
      <DialogBody>
        <section className="table-parsed-file -errors-column">
          {Object.keys(attendantsWithApiErrosAndErrors?.[0])?.map(
            (key) => (
              <div
                key={key}
                className="table-parsed-file__item font-weight-bold"
              >
                {key}
              </div>
            )
          )}
          {attendantsWithApiErrosAndErrors?.map((row) => (
            <Fragment key={row?.PIN}>
              <div className="table-parsed-file__item">
                {row['First and Last Name']}
              </div>
              <div
                className={cn('table-parsed-file__item', {
                  'color-danger': row['errors']?.some((error) =>
                    [
                      INVALID_PIN_LENGTH,
                      DUPLICATE_PIN,
                      EXISTING_PIN,
                    ].includes(error)
                  ),
                })}
              >
                {row['PIN']}
              </div>
              <div
                className={cn('table-parsed-file__item', {
                  'color-danger':
                    row['errors']?.includes(INVALID_ROLE),
                })}
              >
                {row['Role']}
              </div>
              <div className="table-parsed-file__item color-danger flex-direction-column align-items-flex-start justify-content-center">
                {row.errors?.map((error) => {
                  return (
                    <div>
                      {orgAttendantsFileErrorMessages?.get(error)}
                    </div>
                  )
                })}
              </div>
            </Fragment>
          ))}
        </section>
      </DialogBody>
      <DialogActions>
        <div className="upload-file__actions">
          <button
            type="button"
            onClick={() =>
              setStep(UPLOAD_ORG_ATTENDANTS_STEPS.UPLOADED_FILE)
            }
            className="upload-file__button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="upload-file__button"
            onClick={handleExportAsCSV}
          >
            Export & Close
          </button>
          {/* eslint-disable-next-line */}
          <a ref={downloadLinkRef}></a>
        </div>
      </DialogActions>
    </>
  )
}

ErrorsPreview.propTypes = {
  parsedFile: PropTypes.array,
  attendantsWithApiErrors: PropTypes.array,
  setStep: PropTypes.func,
  handleUploadDialogClose: PropTypes.func,
}

export default ErrorsPreview
